import { Fullscreen, FullscreenExit } from "@mui/icons-material";
import { Box, ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import dico from "../../../util/src/language/dico.js";
import {
  getActiveFullscreenElement,
  RUNTIME_LAYOUT_CONTEXT_MENU_HEIGHT,
  RUNTIME_LAYOUT_CONTEXT_MENU_WIDTH,
  RUNTIME_LAYOUT_CONTEXT_MENU_Z_INDEX,
  toggleElementFullscreen,
} from "./runtimeFullscreen.js";

type MenuPosition = {
  mouseX: number;
  mouseY: number;
};

/**
 * Runtime context menu (right-click) with fullscreen toggle for layout canvases.
 */
export function useRuntimeLayoutFullscreenMenu(
  canvasRef: RefObject<HTMLDivElement | null>,
  isEditor: boolean,
) {
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const [menuState, setMenuState] = useState<MenuPosition | null>(null);
  const [isCanvasFullscreen, setIsCanvasFullscreen] = useState(false);
  const editorDico = dico().licensedPortal.layoutEditor;

  useEffect(() => {
    if (isEditor) {
      setIsCanvasFullscreen(false);
      setMenuState(null);
      return;
    }

    const updateFullscreenState = () => {
      const fullscreenElement = getActiveFullscreenElement();
      setIsCanvasFullscreen(fullscreenElement === canvasRef.current);
      setMenuState(null);
    };

    updateFullscreenState();
    window.document.addEventListener("fullscreenchange", updateFullscreenState);
    window.document.addEventListener(
      "webkitfullscreenchange",
      updateFullscreenState as EventListener,
    );

    return () => {
      window.document.removeEventListener(
        "fullscreenchange",
        updateFullscreenState,
      );
      window.document.removeEventListener(
        "webkitfullscreenchange",
        updateFullscreenState as EventListener,
      );
    };
  }, [canvasRef, isEditor]);

  useEffect(() => {
    if (menuState == null) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        setMenuState(null);
        return;
      }
      if (contextMenuRef.current?.contains(target)) return;
      setMenuState(null);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuState(null);
      }
    };

    window.document.addEventListener("pointerdown", handlePointerDown);
    window.document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.document.removeEventListener("pointerdown", handlePointerDown);
      window.document.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuState]);

  const handleOpenRuntimeContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (isEditor) return;
      event.preventDefault();
      const canvasRect = canvasRef.current?.getBoundingClientRect();
      setMenuState({
        mouseX: canvasRect
          ? event.clientX - canvasRect.left + 2
          : event.clientX + 2,
        mouseY: canvasRect
          ? event.clientY - canvasRect.top - 6
          : event.clientY - 6,
      });
    },
    [canvasRef, isEditor],
  );

  const handleCloseRuntimeContextMenu = useCallback(() => {
    setMenuState(null);
  }, []);

  const handleToggleFullscreen = useCallback(async () => {
    const canvasElement = canvasRef.current;
    handleCloseRuntimeContextMenu();
    if (!canvasElement) return;

    try {
      await toggleElementFullscreen(canvasElement);
    } catch {
      // Ignore browser fullscreen errors to keep the renderer functional.
    }
  }, [canvasRef, handleCloseRuntimeContextMenu]);

  const runtimeContextMenuPosition = useMemo(() => {
    if (menuState == null) return null;
    const canvasRect = canvasRef.current?.getBoundingClientRect();
    if (!canvasRect) {
      return {
        left: menuState.mouseX,
        top: menuState.mouseY,
      };
    }

    return {
      left: Math.min(
        Math.max(0, menuState.mouseX),
        Math.max(0, canvasRect.width - RUNTIME_LAYOUT_CONTEXT_MENU_WIDTH),
      ),
      top: Math.min(
        Math.max(0, menuState.mouseY),
        Math.max(0, canvasRect.height - RUNTIME_LAYOUT_CONTEXT_MENU_HEIGHT),
      ),
    };
  }, [canvasRef, menuState]);

  const contextMenu =
    !isEditor && runtimeContextMenuPosition ? (
      <Box
        ref={contextMenuRef}
        onContextMenu={(event) => event.preventDefault()}
        sx={{
          position: "absolute",
          left: runtimeContextMenuPosition.left,
          top: runtimeContextMenuPosition.top,
          width: RUNTIME_LAYOUT_CONTEXT_MENU_WIDTH,
          zIndex: RUNTIME_LAYOUT_CONTEXT_MENU_Z_INDEX,
          backgroundColor: "#fff",
          borderRadius: 1,
          boxShadow: 8,
          border: "1px solid rgba(0,0,0,0.12)",
          overflow: "hidden",
        }}
      >
        <MenuItem onClick={() => void handleToggleFullscreen()}>
          <ListItemIcon>
            {isCanvasFullscreen ? (
              <FullscreenExit fontSize="small" />
            ) : (
              <Fullscreen fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {isCanvasFullscreen
              ? editorDico.exitFullscreen
              : editorDico.fullscreen}
          </ListItemText>
        </MenuItem>
      </Box>
    ) : null;

  return {
    handleOpenRuntimeContextMenu,
    contextMenu,
  };
}
